import { type ComponentType, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, Link, useRouter, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { toaster } from '@/shared/ui/chakra/toaster';
import { useAdminEventStream } from '@/shared/lib/sse/useEventStream';
import type { SseEvent } from '@/shared/lib/sse/parser';
import {
  LuBuilding2,
  LuClipboardList,
  LuFileText,
  LuFilter,
  LuFuel,
  LuIdCard,
  LuLayoutDashboard,
  LuLibrary,
  LuMap,
  LuMessageSquare,
  LuTag,
  LuUsers,
} from 'react-icons/lu';
import { Avatar } from '@/shared/ui/chakra/avatar';
import { LogoMark } from '@/shared/ui/Logo';
import { ColorModeButton, useColorModeValue } from '@/shared/ui/chakra/color-mode';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/shared/ui/chakra/menu';
import { useAuthStore } from '@/shared/lib/auth/store';
import { chatApi } from '@/features/chat/api';
import { logout } from '@/features/auth/api/logout';
import { NotificationsToggle } from '@/features/notifications/ui/NotificationsToggle';
import { shouldSignalChat } from './chat-signal';

/* App layout shown on every authenticated page: a fixed sidebar with
   icon nav + a 64px top bar carrying the theme toggle, user identity,
   and an account menu. Sub-pages render into <Outlet/>. */

interface NavItem {
  to: string;
  i18nKey: string;
  icon: ComponentType<{ size?: number | string }>;
}

/** Short WebAudio chime for an incoming operator message. Best-effort: if the
    browser blocks autoplay before a user gesture, or lacks AudioContext, it
    silently no-ops. A cue the dispatcher can HEAR when the tab is backgrounded,
    where the toast/badge are invisible. */
let audioCtx: AudioContext | null = null;
function playChime(): void {
  try {
    const Ctor = window.AudioContext;
    if (!Ctor) return;
    audioCtx ??= new Ctor();
    const ctx = audioCtx;
    // If the context was created while suspended (no prior user gesture), it
    // self-heals once activation exists; resume() is a no-op when running.
    void ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.26);
  } catch {
    // autoplay blocked / unsupported — the visual cues still fire
  }
}

/**
 * Global "new dispatcher chat activity" signal. Outside /chat there was no indication a message
 * arrived — the badge on the "Чат" nav item + a toast + a chime cover that. The badge is
 * SERVER-DERIVED (summed unread from GET /v1/admin/chats), not a local counter, so it survives
 * reloads, reflects partial reads, reconciles on reconnect, and never under-reports messages that
 * arrived while the tab was away. See [[project_pwa_webpush]] for the OS-level background path.
 */
function useChatSignal(): number {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const [unread, setUnread] = useState(0);
  const inFlight = useRef(false);
  const pending = useRef(false);
  const refetchRef = useRef<() => void>(() => {});

  // Server-authoritative unread total. Every path that could change it
  // (mount, focus, route change, live event, SSE reconnect) re-derives from here.
  // Coalesced: only one inbox fetch is ever in flight; if triggers arrive during
  // it, exactly one more runs afterward. This removes both the request storm (a
  // burst of N messages → 1 fetch, not N) and out-of-order writes (only one
  // response is ever outstanding, so a stale response can't clobber a newer one).
  const refetchUnread = useCallback(async () => {
    if (inFlight.current) {
      pending.current = true;
      return;
    }
    inFlight.current = true;
    try {
      const rows = await chatApi.inbox();
      setUnread(rows.reduce((sum, r) => sum + (r.unread ?? 0), 0));
    } catch {
      // best-effort — keep the last known value rather than resetting to 0
    } finally {
      inFlight.current = false;
      if (pending.current) {
        pending.current = false;
        void refetchRef.current();
      }
    }
  }, []);
  refetchRef.current = refetchUnread;

  // Hydrate on mount + whenever the tab regains focus (a wake/tab-switch may
  // have missed live events) or a thread is marked read on /chat (ChatPage
  // dispatches `chat:read`, which the shell badge can't see otherwise).
  useEffect(() => {
    void refetchUnread();
    const onWake = () => void refetchUnread();
    window.addEventListener('focus', onWake);
    window.addEventListener('chat:read', onWake);
    return () => {
      window.removeEventListener('focus', onWake);
      window.removeEventListener('chat:read', onWake);
    };
  }, [refetchUnread]);

  // Re-derive on route change: after reading threads on /chat and navigating
  // away, the server unread drops — the badge must follow (not hard-reset to 0).
  useEffect(() => {
    void refetchUnread();
  }, [pathname, refetchUnread]);

  // Flash the tab title with the unread count while the tab is backgrounded, so
  // a dispatcher looking at another window still sees something waiting.
  useEffect(() => {
    const base = t('common.app_name');
    const update = () => {
      document.title = unread > 0 && document.hidden ? `(${unread}) ${base}` : base;
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      document.title = base;
    };
  }, [unread, t]);

  const onEvent = useCallback(
    (ev: SseEvent) => {
      const { signal, isApproval } = shouldSignalChat(ev);
      if (!signal) return;
      // Reconcile the badge from the server on any qualifying event.
      void refetchUnread();
      // Perceptible cue for the message itself — but not while the dispatcher
      // is already looking at the chat page.
      if (pathRef.current === '/chat') return;
      toaster.create({
        title: isApproval ? t('chat.new_approval_title') : t('chat.new_message_title'),
        type: 'info',
      });
      playChime();
    },
    [t, refetchUnread],
  );
  // Resync on (re)connect too — fills any gap the 60s replay buffer missed.
  useAdminEventStream(onEvent, () => void refetchUnread());

  return unread;
}

const NAV_SECTIONS: ReadonlyArray<readonly NavItem[]> = [
  [
    { to: '/', i18nKey: 'nav.dashboard', icon: LuLayoutDashboard },
    { to: '/orders', i18nKey: 'nav.orders', icon: LuClipboardList },
    { to: '/map', i18nKey: 'nav.map', icon: LuMap },
  ],
  [
    { to: '/operators', i18nKey: 'nav.operators', icon: LuUsers },
    { to: '/providers', i18nKey: 'nav.providers', icon: LuBuilding2 },
    { to: '/clients', i18nKey: 'nav.clients', icon: LuIdCard },
    { to: '/catalog', i18nKey: 'nav.catalog', icon: LuLibrary },
    { to: '/pricing', i18nKey: 'nav.pricing', icon: LuTag },
    { to: '/rules', i18nKey: 'nav.rules', icon: LuFilter },
  ],
  [
    { to: '/refill-requests', i18nKey: 'nav.refill_requests', icon: LuFuel },
    { to: '/chat', i18nKey: 'nav.chat', icon: LuMessageSquare },
    { to: '/reports', i18nKey: 'nav.reports', icon: LuFileText },
  ],
];

export function AppShell() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const reset = useAuthStore((s) => s.reset);
  const router = useRouter();
  const logoTone = useColorModeValue<'brand' | 'onDark'>('brand', 'onDark');
  const chatUnread = useChatSignal();

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      reset();
      await router.navigate({ to: '/login' });
    }
  };

  return (
    <Flex h="100vh">
      <Box
        as="aside"
        aria-label={t('layout.sidebar_label')}
        w="260px"
        flexShrink={0}
        borderRightWidth="1px"
        borderColor="border"
        bg="bg.panel"
        p="4"
        display="flex"
        flexDirection="column"
        gap="6"
        overflowY="auto"
      >
        <Flex align="center" gap="2.5" px="2">
          <LogoMark size={26} tone={logoTone} />
          <Heading size="md" color="colorPalette.fg">
            {t('common.app_name')}
          </Heading>
        </Flex>
        <Stack as="nav" gap="6">
          {NAV_SECTIONS.map((section, idx) => (
            <Stack key={idx} gap="1">
              {section.map((item) => (
                <NavLink
                  key={item.to}
                  item={item}
                  label={t(item.i18nKey)}
                  badge={item.to === '/chat' ? chatUnread : 0}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      </Box>

      <Flex direction="column" flex="1" minH="0">
        <Flex
          as="header"
          align="center"
          justify="flex-end"
          gap="3"
          px="6"
          h="64px"
          flexShrink={0}
          borderBottomWidth="1px"
          borderColor="border"
          bg="bg.panel"
        >
          <NotificationsToggle />
          <ColorModeButton />
          <Text fontSize="sm" color="fg.muted">
            {user ? `${user.username} · ${user.role}` : ''}
          </Text>
          <MenuRoot>
            <MenuTrigger asChild>
              <Box as="button" aria-label={t('common.account')} cursor="pointer" rounded="full">
                <Avatar size="sm" name={user?.username ?? ''} />
              </Box>
            </MenuTrigger>
            <MenuContent>
              <MenuItem value="sign-out" onClick={handleSignOut}>
                {t('common.sign_out')}
              </MenuItem>
            </MenuContent>
          </MenuRoot>
        </Flex>

        <Box as="main" flex="1" overflowY="auto" p="6" bg="bg.subtle">
          <Outlet />
        </Box>
      </Flex>
    </Flex>
  );
}

function NavLink({ item, label, badge }: { item: NavItem; label: string; badge: number }) {
  const Icon = item.icon;
  return (
    <Link to={item.to} style={{ textDecoration: 'none' }} activeOptions={{ exact: item.to === '/' }}>
      {({ isActive }) => (
        <Flex
          align="center"
          gap="3"
          px="3"
          py="2"
          rounded="md"
          fontSize="sm"
          fontWeight={isActive ? 'semibold' : 'normal'}
          color={isActive ? 'colorPalette.fg' : 'fg'}
          bg={isActive ? 'colorPalette.subtle' : 'transparent'}
          transition="background 0.12s"
          _hover={{ bg: isActive ? 'colorPalette.subtle' : 'bg.muted' }}
        >
          <Icon size={18} />
          <Text>{label}</Text>
          {badge > 0 ? (
            <Badge ml="auto" colorPalette="red" borderRadius="full" fontSize="0.7rem">
              {badge > 99 ? '99+' : badge}
            </Badge>
          ) : null}
        </Flex>
      )}
    </Link>
  );
}
